import { type FC, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { layoutStore } from 'shared/ui'
import { type IColumn, Table, Pagination } from 'shared/ui/TableNew'
import { getCellForNameDefault } from 'shared/helpers/getCellForNameDefault'
import { type RowContact } from 'entities/Contacts/model/Table/RowContact'
import {
  getCellForTags,
  getCellNumber,
} from 'widgets/ContactEnrollment/ui/EnrollmentContactsTable/helpers/helpers'
import type { EnrollmentListStore } from 'widgets/ContactEnrollment/store'
import { EnrollmentActions } from '../../EnrollmentActions'
import styles from './styles.module.scss'

type IEnrollmentContactsTableProps = {
  enrollmentListStore: EnrollmentListStore
}

export const EnrollmentContactsTable: FC<IEnrollmentContactsTableProps> = observer(
  ({ enrollmentListStore }) => {
    const { isViewXXsGlobal } = layoutStore
    const { contactsTableStore, allTotalCount } = enrollmentListStore
    const { tableStore } = contactsTableStore

    useEffect(() => {
      if (allTotalCount) {
        contactsTableStore.setFilter(
          enrollmentListStore.filters,
          enrollmentListStore.excludeFilters
        )
        contactsTableStore.fetchContacts()
      }

      return () => {
        contactsTableStore.reset()
      }
    }, [])

    const rows = useMemo(
      () =>
        contactsTableStore.items.map((item) => {
          item.setAriaLabel('ContactItemRow')

          return item
        }),
      [contactsTableStore.items]
    )

    const columns: IColumn<RowContact>[] = [
      {
        field: 'name',
        name: 'Name',
        width: '100%',
        minWidth: '278px',
        maxWidth: '278px',
        renderRowCell: (row) =>
          getCellForNameDefault({
            fullName: row.origin.full_name,
            avatarInfo: row.origin.avatarInfo,
            width: isViewXXsGlobal ? 180 : undefined,
          }),
      },
      {
        field: 'number',
        name: 'Phone',
        renderRowCell: (row) => getCellNumber(row),
      },
      {
        field: 'email',
        name: 'Email',
      },
      {
        field: 'tag',
        name: 'Tags',
        renderRowCell: (row) =>
          getCellForTags({
            tags: row.origin.tags,
            isDisabled: row.origin.isDisabled,
          }),
      },
    ]

    return (
      <div className={styles.contentContainer}>
        <div className={styles.mainContainer}>
          <section className={styles.previewSection}>
            <Table
              virtualization
              ariaLabel={'EnrollmentContactsTable'}
              store={tableStore}
              rows={rows}
              columns={columns}
              pinnedColumnIds={['name']}
              hidePagination
              emptyStateProps={{
                iconProps: {
                  icon: 'contacts',
                  color: 'var(--content-neutral-primary)',
                },
                text: 'No matching contacts',
              }}
            />
          </section>

          <footer className={styles.footerActions}>
            {!!tableStore.total && (
              <Pagination
                initPage={tableStore.page}
                total={tableStore.total}
                limit={tableStore.limit}
                onChange={contactsTableStore.onChangePagination}
              />
            )}
            <EnrollmentActions />
          </footer>
        </div>
      </div>
    )
  }
)
